/* Small building blocks for the compact Mass Messages pages that the shared
   Chat Assistant kit doesn't have: an icon menu button, a − value + stepper,
   a checkbox with a partial state, a section header and a modal shell. */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../ChatAssistant/compact/kit';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import '../../ChatAssistant/compact/compact.css';

export interface MenuOpt { id: string; label: ReactNode; sub?: ReactNode; checked?: boolean }

type MenuLook = 'field' | 'dashed' | 'tinted';

/** A button that opens a small option menu (filters, "Add vault", "Add list"…). */
export function Menu({ label, icon, iconColor, options, onPick, look = 'field', width = 220, align = 'left', disabled, height = 28, empty }: {
  label: ReactNode; icon?: string; iconColor?: string; options: MenuOpt[]; onPick: (id: string) => void;
  look?: MenuLook; width?: number; align?: 'left' | 'right'; disabled?: boolean; height?: number; empty?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    // Capture phase: Esc closes this menu only, not the dialog it may sit in.
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', down);
    window.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', down); window.removeEventListener('keydown', key, true); };
  }, [open]);
  const looks: Record<MenuLook, CSSProperties> = {
    field: { background: C.input, border: `1px solid ${C.border}`, color: C.text },
    tinted: { background: C.tint16, border: '1px solid color-mix(in srgb, var(--om-accent) 40%, transparent)', color: C.text },
    dashed: { background: 'transparent', border: `1px dashed ${C.borderHi}`, color: C.purpleLt },
  };
  const showChecks = options.some((o) => o.checked !== undefined);
  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={look === 'dashed' ? 'cpt-hover' : undefined}
        style={{ height, padding: look === 'dashed' ? '0 9px' : '0 10px', display: 'flex', alignItems: 'center', gap: look === 'dashed' ? 6 : 7, borderRadius: 7, font: f(500, 12), cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1, ...looks[look] }}
      >
        {look === 'dashed' ? <Icon name="plus" size={12} /> : icon && <Icon name={icon} size={13} color={iconColor ?? C.muted} />}
        {label}
        {look !== 'dashed' && <Icon name="chevron-down" size={13} color={C.muted} />}
      </button>
      {open && (
        <div className="cpt-thin-scroll" style={{ position: 'absolute', top: height + 4, [align]: 0, zIndex: 41, width, maxHeight: 260, overflowY: 'auto', padding: 4, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map((o) => (
            <button key={o.id} type="button" className="cpt-hover-row" onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(o.id); }} style={{ minHeight: 30, padding: '5px 8px', border: 0, borderRadius: 5, background: 'transparent', color: C.text, font: f(500, 12), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
              {showChecks && <Icon name="check" size={13} color={C.purpleLt} style={{ opacity: o.checked ? 1 : 0, flex: 'none' }} />}
              <span style={{ flex: 1, minWidth: 0 }}>{o.label}</span>
              {o.sub !== undefined && o.sub !== '' && <span style={{ color: C.muted, font: f(400, 11), whiteSpace: 'nowrap' }}>{o.sub}</span>}
            </button>
          ))}
          {!options.length && empty && <span style={{ padding: '7px 8px', font: f(400, 12), color: C.muted }}>{empty}</span>}
        </div>
      )}
    </div>
  );
}

/** − value + pill (weights, minutes). */
export function Stepper({ value, onDec, onInc, minWidth = 30 }: { value: ReactNode; onDec: () => void; onInc: () => void; minWidth?: number }) {
  const b: CSSProperties = { width: 24, height: 26, border: 0, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  return (
    <div style={{ height: 28, flex: 'none', boxSizing: 'border-box', display: 'flex', alignItems: 'center', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7 }}>
      <button type="button" onClick={onDec} className="cpt-hover-text" style={b}><Icon name="minus" size={12} /></button>
      <span style={{ minWidth, textAlign: 'center', font: f(600, 12), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <button type="button" onClick={onInc} className="cpt-hover-text" style={b}><Icon name="plus" size={12} /></button>
    </div>
  );
}

/** 16px checkbox; `partial` draws a dash (some of a day's rows picked). */
export function Check({ on, partial, onClick, disabled, title }: { on: boolean; partial?: boolean; onClick: () => void; disabled?: boolean; title?: string }) {
  const lit = on || partial;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on ? true : partial ? 'mixed' : false}
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ width: 16, height: 16, flex: 'none', padding: 0, boxSizing: 'border-box', borderRadius: 4, border: `1px solid ${lit ? C.purple : 'var(--om-border-strong)'}`, background: lit ? C.purple : 'transparent', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
    >
      {lit && <Icon name={partial && !on ? 'minus' : 'check'} size={11} />}
    </button>
  );
}

/** Page section title + one-line summary, with actions on the right. */
export function SectionHead({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 220 }}>
        <span style={{ font: f(700, 17, 1.2), color: C.text }}>{title}</span>
        {sub && <span style={{ font: f(400, 12), color: C.sub }}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

/** 28px square icon button (edit, delete…). */
export function IconBtn({ icon, title, onClick, disabled, size = 28, iconSize = 13, color = C.sub }: { icon: string; title: string; onClick: () => void; disabled?: boolean; size?: number; iconSize?: number; color?: string }) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={(e) => { e.stopPropagation(); onClick(); }} className={disabled ? undefined : 'cpt-hover-icon'} style={{ width: size, height: size, flex: 'none', border: 0, borderRadius: 6, background: 'transparent', color, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={icon} size={iconSize} />
    </button>
  );
}

/** Centered modal on a dimmed backdrop; Esc closes it unless `locked`. */
export function Dialog({ width, onClose, children, z = 1000, closeOnBackdrop, locked, style }: { width: number; onClose: () => void; children: ReactNode; z?: number; closeOnBackdrop?: boolean; locked?: boolean; style?: CSSProperties }) {
  useEffect(() => {
    if (locked) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose, locked]);
  return createPortal(
    <div className="cpt-root" onClick={closeOnBackdrop && !locked ? onClose : undefined} style={{ position: 'fixed', inset: 0, zIndex: z, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width, maxWidth: '100%', maxHeight: 'calc(100 * var(--om-vh) - 48px)', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,.6)', display: 'flex', flexDirection: 'column', ...style }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Dialog header: icon tile, title + subtitle, close button. */
export function DialogHead({ icon, title, sub, onClose, closeTitle = '' }: { icon?: string; title: ReactNode; sub?: ReactNode; onClose?: () => void; closeTitle?: string }) {
  return (
    <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 12px 14px 18px', borderBottom: `1px solid ${C.divider}` }}>
      {icon && <span style={{ width: 32, height: 32, flex: 'none', borderRadius: 8, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={16} /></span>}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ font: f(700, 15), color: C.text }}>{title}</span>
        {sub && <span style={{ font: f(400, 12), color: C.sub }}>{sub}</span>}
      </div>
      {onClose && <IconBtn icon="x" title={closeTitle} onClick={onClose} iconSize={15} />}
    </div>
  );
}

/** Rounded inner card used inside dialogs and expanded lists (darker than the page card). */
export function Panel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className="cpt-card" style={{ background: C.input, border: `1px solid ${C.borderLo}`, borderRadius: 8, ...style }}>{children}</div>;
}

/** A row inside a Panel: label + helper on the left, control on the right. */
export function PanelRow({ label, help, children, minHeight = 44 }: { label: ReactNode; help?: ReactNode; children?: ReactNode; minHeight?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight, padding: '8px 12px', flexWrap: 'wrap', boxSizing: 'border-box' }}>
      <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ font: f(500, 12.5), color: C.text }}>{label}</span>
        {help && <span style={{ font: f(400, 11.5), color: C.sub }}>{help}</span>}
      </div>
      {children}
    </div>
  );
}

/** Small heading above a block: "Add messages  Separate with semicolons…". */
export function BlockHead({ title, hint, right }: { title: ReactNode; hint?: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
      {hint && <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>}
      {right && <><span style={{ flex: 1 }} />{right}</>}
    </div>
  );
}

/** Count chip next to a heading or a tab. */
export function CountChip({ children, tone = 'purple' }: { children: ReactNode; tone?: 'purple' | 'dark' }) {
  return <span style={{ font: f(600, 10.5), color: C.purplePale, background: tone === 'purple' ? C.tint16 : C.input, padding: '1px 6px', borderRadius: 99, fontVariantNumeric: 'tabular-nums' }}>{children}</span>;
}

/** Striped placeholder tile (vault chip, media without a thumbnail). */
export const STRIPES = 'repeating-linear-gradient(135deg,var(--om-seg) 0 3px,var(--om-legacy-active) 3px 6px)';
